/**
 * Rhythmz — Scene: Kaleidoscopic Torus
 * ──────────────────────────────────
 * 5 interlocking torus knots at different sizes/rotations with independent
 * color cycling. Full spectrum, fast rotation, bass-reactive pulsing.
 *
 * Audio: Bass → pulse size. Beat → color snap. Mid → rotation speed.
 */

import * as THREE from 'three/webgpu';
import { Fn, vec4, float, fract, sin, add, mul, positionLocal } from 'three/tsl';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface TorusUniforms { uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N; uCol0: N; uCol1: N; uCol2: N; }

export function createKaleidoscopicTorus(u: TorusUniforms): SceneHandle {
  const group = new THREE.Group();

  const knotConfigs = [
    { r: 0.3, tube: 0.08, p: 2, q: 3, speed: 1.0 },
    { r: 0.5, tube: 0.06, p: 3, q: 7, speed: 0.7 },
    { r: 0.7, tube: 0.05, p: 2, q: 5, speed: 0.5 },
    { r: 0.9, tube: 0.04, p: 3, q: 4, speed: 0.4 },
    { r: 1.1, tube: 0.035, p: 5, q: 3, speed: 0.3 },
  ];

  const knots: THREE.Mesh[] = [];
  knotConfigs.forEach((cfg, i) => {
    const geo = new THREE.TorusKnotGeometry(cfg.r, cfg.tube, 200, 32, cfg.p, cfg.q);
    const mat = new THREE.MeshPhysicalNodeMaterial({
      metalness: 0.7, roughness: 0.1, clearcoat: 0.5,
      emissive: new THREE.Color(1, 0.3, 0.7),
      emissiveIntensity: 3,
    });
    mat.emissiveNode = Fn(() => {
      const idx = float(i);
      const colT = fract(add(mul(u.uTime, add(0.08, mul(idx, 0.02))), mul(positionLocal.x, 0.15)));
      const col = vec4(
        add(0.5, mul(sin(mul(colT, 6.28)), 0.5)),
        add(0.5, mul(sin(add(mul(colT, 6.28), 2.094)), 0.5)),
        add(0.5, mul(sin(add(mul(colT, 6.28), 4.188)), 0.5)),
        1,
      );
      const beatPulse = add(0.8, mul(u.uBeat, 4));
      return mul(col, beatPulse);
    })();
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(0, 0, 0);
    group.add(mesh);
    knots.push(mesh);
  });

  // ── Central glow sphere ──
  const coreGeo = new THREE.IcosahedronGeometry(0.15, 2);
  const coreMat = new THREE.MeshPhysicalNodeMaterial({ metalness: 0, roughness: 0, emissive: 0xff44aa, emissiveIntensity: 3 });
  const core = new THREE.Mesh(coreGeo, coreMat);
  group.add(core);

  let t = 0;

  const update = (dt: number): void => {
    t += dt;
    const bass = u.uBass.value ?? 0;
    const mid = u.uMid.value ?? 0;
    const beat = u.uBeat.value ?? 0;

    knots.forEach((k, i) => {
      const cfg = knotConfigs[i];
      const speed = cfg.speed * (0.5 + mid * 2);
      k.rotation.x = t * speed * 0.7;
      k.rotation.y = t * speed * 1.3;
      k.rotation.z = t * speed * 0.4;
      const pulse = 1 + bass * 0.2 * (1 - i * 0.1);
      k.scale.setScalar(pulse);
    });

    core.scale.setScalar(1 + beat * 0.5);
    core.material.emissiveIntensity = 2 + beat * 4;

    group.rotation.y = t * 0.08;
    group.rotation.x = Math.sin(t * 0.05) * 0.2;
  };

  const dispose = (): void => {
    knots.forEach(k => { k.geometry.dispose(); (k.material as THREE.Material).dispose(); });
    coreGeo.dispose(); coreMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Kaleidoscopic Torus' };
}